import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { MailX, Plus, RefreshCw, Search, Upload, Users } from 'lucide-react';
import { z } from 'zod';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ContractStatusBadge } from '@/components/ui/ContractStatusBadge';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { CustomerFormDialog } from '@/components/customers/CustomerFormDialog';
import { useArchivedCustomerCount, useCustomers } from '@/hooks/useCustomers';
import { useCursorPager } from '@/hooks/useCursorPager';
import { formatDateTime } from '@/lib/format';
import type { CustomersFilter } from '@/hooks/useCustomers';
import type { Customer } from '@/types/api';

/**
 * Customer-Liste (WT-102 + Sprint-1-Carry-Over).
 *
 * - URL ist Source of Truth für Such-/Cursor-State (deep-linkbar, Browser-Back funktioniert).
 * - Cursor-Pagination per "Mehr laden" - keine Page-Numbers (Backend nutzt Keyset).
 * - Aggregierte Stats (locations_count, systems_count, last_maintenance_at,
 *   active_contract_status) kommen über `?expand=stats` aus EINER Query
 *   (kein N+1, Backend macht Subquery).
 */

const searchSchema = z.object({
  q: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  // Deep-linkbar wie die Suche: der 04.09.2026-Vorfall hat gezeigt, dass ein
  // archivierter Kunde sonst nur über die URL auffindbar ist — dann soll
  // die Ansicht auch sagen, dass man im Archiv ist.
  archiv: z.boolean().optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/customers/')({
  component: CustomersPage,
  validateSearch: (s) => searchSchema.parse(s),
});

function listSearch(opts: {
  q?: string | undefined;
  archivAnsicht: boolean;
  limit?: number | undefined;
  cursor?: string | undefined;
}): SearchParams {
  return {
    ...(opts.q ? { q: opts.q } : {}),
    ...(opts.archivAnsicht ? { archiv: true } : {}),
    ...(opts.limit !== undefined ? { limit: opts.limit } : {}),
    ...(opts.cursor ? { cursor: opts.cursor } : {}),
  };
}

function CustomersPage(): JSX.Element {
  const { q, cursor, archiv, limit: limitParam } = Route.useSearch();
  const navigate = useNavigate({ from: '/customers/' });

  const [searchInput, setSearchInput] = useState(q ?? '');
  const [createOpen, setCreateOpen] = useState(false);

  const archivAnsicht = archiv === true;
  const limit = limitParam ?? 50;
  const filter: CustomersFilter = {
    limit,
    expand: 'stats',
    archived: archivAnsicht, // false = ausdrücklich nur aktive
    ...(q ? { search: q } : {}),
    ...(cursor ? { cursor } : {}),
  };

  const query = useCustomers(filter);
  const archivedCount = useArchivedCustomerCount();

  const onSearch = (event: React.FormEvent): void => {
    event.preventDefault();
    const next = listSearch({
      q: searchInput || undefined,
      archivAnsicht,
      limit: limitParam,
    });
    void navigate({ search: () => next });
  };

  const setArchivAnsicht = (nextArchiv: boolean): void => {
    // Cursor einer Liste ist in der anderen sinnlos — Keyset-Position
    // aus den Aktiven zeigt mitten ins Archiv (oder ins Leere).
    void navigate({
      search: () => listSearch({ q, archivAnsicht: nextArchiv, limit: limitParam }),
    });
  };

  const onCursorChange = useCallback(
    (nextCursor: string | undefined) => {
      void navigate({
        search: () =>
          listSearch({
            q,
            archivAnsicht,
            limit: limitParam,
            cursor: nextCursor,
          }),
      });
    },
    [archivAnsicht, limitParam, navigate, q]
  );

  const onPageSizeChange = useCallback(
    (size: number) => {
      void navigate({
        search: () => listSearch({ q, archivAnsicht, limit: size }),
      });
    },
    [archivAnsicht, navigate, q]
  );

  const shown = query.data?.data.length ?? 0;
  const total = query.data?.meta?.total_estimate ?? null;
  const totalLabel =
    total != null
      ? `~${total} Einträge gesamt - ${shown} angezeigt`
      : `${shown} Einträge angezeigt`;

  const server = useCursorPager({
    cursor,
    nextCursor: query.data?.links?.next ?? null,
    onCursorChange,
    pageSize: limit,
    onPageSizeChange,
    totalLabel,
  });

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Kunden</h1>
          <p className="text-muted">
            {archivAnsicht
              ? 'Archivierte Kunden — ausgeblendet aus allen Listen, jederzeit wiederherstellbar.'
              : 'Source-of-Truth für Kundenverzeichnis.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => void query.refetch()}
            leftIcon={<RefreshCw className="size-4" />}
          >
            Aktualisieren
          </Button>
          {!archivAnsicht && (
            <Button
              leftIcon={<Plus className="size-4" />}
              onClick={() => setCreateOpen(true)}
              data-testid="customer-create"
            >
              Neuer Kunde
            </Button>
          )}
        </div>
      </header>

      <CustomerFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={(customer) =>
          void navigate({ to: '/customers/$id', params: { id: customer.id } })
        }
      />

      <Card>
        <form onSubmit={onSearch} className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px] flex-1">
            <Input
              label="Suche"
              placeholder="Name, Slug, Notizen ..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              leftIcon={<Search className="size-4" />}
            />
          </div>
          <Button type="submit">Suchen</Button>
          {q && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearchInput('');
                void navigate({
                  search: () => listSearch({ archivAnsicht, limit: limitParam }),
                });
              }}
            >
              Zurücksetzen
            </Button>
          )}
          <div
            className="ml-auto flex gap-1"
            data-testid="customers-archive-toggle"
            role="group"
            aria-label="Kundenbestand oder Archiv"
          >
            <Button
              type="button"
              variant={archivAnsicht ? 'secondary' : 'primary'}
              data-testid="customers-archive-off"
              onClick={() => setArchivAnsicht(false)}
            >
              Aktiv
            </Button>
            <Button
              type="button"
              variant={archivAnsicht ? 'primary' : 'secondary'}
              data-testid="customers-archive-on"
              onClick={() => setArchivAnsicht(true)}
            >
              {archivedCount == null ? 'Archiv' : `Archiv (${archivedCount})`}
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        {query.isLoading && <SkeletonRows rows={6} cols={6} />}

        {query.isError && <ErrorAlert error={query.error} />}

        {query.data && query.data.data.length === 0 && (
          <EmptyState
            icon={<Users className="size-8" />}
            title={archivAnsicht ? 'Kein archivierter Kunde' : 'Keine Kunden vorhanden'}
            description={
              archivAnsicht
                ? q
                  ? `Kein Treffer für "${q}" im Archiv.`
                  : 'Gelöschte Kunden liegen hier, bis sie wiederhergestellt oder endgültig entfernt werden.'
                : q
                  ? `Kein Treffer für "${q}". Anderen Suchbegriff probieren oder Filter zurücksetzen.`
                  : 'Wartungstabelle (.ods) importieren oder ersten Kunden manuell anlegen.'
            }
            {...(archivAnsicht
              ? {}
              : {
                  actions: (
                    <>
                      <Button
                        leftIcon={<Plus className="size-4" />}
                        onClick={() => setCreateOpen(true)}
                      >
                        Neuer Kunde
                      </Button>
                      <Link to="/settings/import">
                        <Button variant="secondary" leftIcon={<Upload className="size-4" />}>
                          ODS importieren
                        </Button>
                      </Link>
                    </>
                  ),
                })}
          />
        )}

        {query.data && query.data.data.length > 0 && (
          <DataTable
            caption="Kundenliste"
            columns={CUSTOMER_COLUMNS}
            rows={query.data.data}
            rowKey={(customer) => customer.id}
            storageKey="customers"
            server={server}
          />
        )}
      </Card>
    </div>
  );
}

function ohneStrich(text: string): string {
  return text === '–' ? '' : text;
}

/** Gleicher Text wie ContractStatusBadge, damit der Select-Filter stabile Werte hat. */
function vertragFiltertext(customer: Customer): string {
  const status = customer.stats?.active_contract_status;
  if (status === 'mapped') return 'Aktiver Vertrag';
  if (status === 'unmapped') return 'Mapping fehlt';
  if (status === 'none') return 'Kein Vertrag';
  return customer.invoice_ninja_client_id ? 'Mapping fehlt' : 'Kein Vertrag';
}

function CustomerNameCell({ customer }: { customer: Customer }): JSX.Element {
  return (
    <>
      <Link
        to="/customers/$id"
        params={{ id: customer.id }}
        className="font-medium text-primary hover:underline"
      >
        {customer.name}
      </Link>
      {customer.deactivated_at && (
        <Badge tone="warning" className="ml-2">
          {/* "deaktiviert" hat am 04.09.2026 in die Irre geführt: der
              Betreiber sah die gelöschte Winkler-Dublette wie einen
              normalen Kunden. Archiv ist der ehrliche Name. */}
          archiviert
        </Badge>
      )}
      {customer.maintenance_window && customer.maintenance_window !== 'standard' && (
        <Badge tone="primary" className="ml-2">
          {customer.maintenance_window}
        </Badge>
      )}
    </>
  );
}

function CustomerEmpfaengerCell({ customer }: { customer: Customer }): JSX.Element {
  return (
    <span data-testid={`customer-recipients-${customer.id}`}>
      {customer.notifications_opt_out ? (
        <span
          className="inline-flex items-center justify-end text-muted"
          title="Opt-out: keine Wartungsmails"
        >
          <MailX className="size-4" aria-hidden="true" />
          <span className="sr-only">Opt-out</span>
        </span>
      ) : customer.notification_recipient_count === 0 ? (
        <span className="text-warning" title="Keine Wartungsmail-Empfänger hinterlegt">
          0
        </span>
      ) : (
        <span title={`${customer.notification_recipient_count} Wartungsmail-Empfänger`}>
          {customer.notification_recipient_count}
        </span>
      )}
    </span>
  );
}

const CUSTOMER_COLUMNS: ReadonlyArray<Column<Customer>> = [
  {
    id: 'name',
    header: 'Name',
    filterValue: (customer) => customer.name,
    cell: (customer) => <CustomerNameCell customer={customer} />,
  },
  {
    id: 'slug',
    header: 'Slug',
    className: 'font-mono text-xs text-muted',
    cell: (customer) => customer.slug,
  },
  {
    id: 'vertrag',
    header: 'Vertrag',
    filter: 'select',
    filterValue: vertragFiltertext,
    cell: (customer) => (
      <ContractStatusBadge
        status={customer.stats?.active_contract_status}
        fallbackHasContract={Boolean(customer.invoice_ninja_client_id)}
      />
    ),
  },
  {
    id: 'standorte',
    header: 'Standorte',
    headerClassName: 'text-right',
    className: 'text-right',
    filterValue: (customer) => (customer.stats ? String(customer.stats.locations_count) : ''),
    cell: (customer) =>
      customer.stats ? (
        <span>{customer.stats.locations_count}</span>
      ) : (
        <span className="text-muted" title="Stats nicht geladen">
          –
        </span>
      ),
  },
  {
    id: 'systeme',
    header: 'Systeme',
    headerClassName: 'text-right',
    className: 'text-right',
    filterValue: (customer) => (customer.stats ? String(customer.stats.systems_count) : ''),
    cell: (customer) =>
      customer.stats ? (
        <span>
          {customer.stats.systems_count}
          {customer.stats.active_systems_count != null &&
            customer.stats.active_systems_count !== customer.stats.systems_count && (
              <span className="ml-1 text-xs text-muted">({customer.stats.active_systems_count})</span>
            )}
        </span>
      ) : (
        <span className="text-muted">–</span>
      ),
  },
  {
    id: 'empfaenger',
    header: 'Empfänger',
    headerClassName: 'text-right',
    className: 'text-right',
    filterValue: (customer) =>
      customer.notifications_opt_out
        ? 'Opt-out'
        : String(customer.notification_recipient_count ?? ''),
    cell: (customer) => <CustomerEmpfaengerCell customer={customer} />,
  },
  {
    id: 'letzte-wartung',
    header: 'Letzte Wartung',
    className: 'text-muted',
    filterValue: (customer) =>
      ohneStrich(formatDateTime(customer.stats?.last_maintenance_at ?? customer.updated_at)),
    cell: (customer) =>
      formatDateTime(customer.stats?.last_maintenance_at ?? customer.updated_at),
  },
];
